import { AuthDialog } from 'fragments/Auth/AuthDialog'
import React, { useEffect, useMemo, useState } from 'react'

import { useDialog } from '@/components/Dialog/DialogContext'
import { StyledModalBackground } from '@/components/MobileCategoryBrowser/StyledNavigator'
import { MegaMenuContextProvider } from '@/components/NavDropdownMegaMenu/MegaMenuContext'
import { MegaMenuMuiLink } from '@/components/NavDropdownMegaMenu/MegaMenuLink'
import { CategoryTabPanel } from '@/components/NavDropdownMegaMenu/MegaMenuTabPanel'
import { NavDropdownMegaMenu } from '@/components/NavDropdownMegaMenu/NavDropdownMegaMenu'
import { usePixieWorldAuthContext } from '@/shared/auth/pixieWorldAuthContext'
import { useAuthChange } from '@/shared/auth/useAuthChange'
import { pxToRem } from '@/shared/styles/themes'
import { Grid, styled } from '@mui/material'

import { GlobalContextType } from './global/layout'
import { getUrlFromMenuItem } from './libs/getUrlFromMenuItem'
import { PrimaryNavigation, PrimaryNavigationProps } from './PrimaryNavigation/PrimaryNavigation'
import { PrimaryNavigationContext } from './PrimaryNavigation/PrimaryNavigationContext'
import { MobileSearchBarModal } from './SearchBar/MobileSearchBarModal'

export type HeaderFragmentProps = Pick<PrimaryNavigationProps, 'transparent' | 'staticHeader' | 'scrollWithPage'> & {
  header?: GlobalContextType['header']
}

export const HeaderFragment: React.FC<HeaderFragmentProps> = ({ header, ...props }) => {
  const [authenticated, setAuthenticated] = useState<boolean>(false)
  const { openDialog } = useDialog()
  const { getAuthState } = usePixieWorldAuthContext()
  const authState = getAuthState()
  const [isSearchOpen, setSearchOpen] = useState(false)
  const [isMobileCategoryBrowserOpen, setMobileCategoryBrowserOpen] = useState(false)

  useAuthChange({
    onSignedOut: () => {
      setAuthenticated(getAuthState() === 'signedIn')
    },
    onSignedIn: () => {
      setAuthenticated(getAuthState() === 'signedIn')
    },
  })

  const onCloseMobileCategoryBrowser = () => {
    setMobileCategoryBrowserOpen(false)
  }

  const onOpenAuthDialog = () => {
    openDialog(AuthDialog, {
      onClose: () => {},
    })
  }

  useEffect(() => {
    setAuthenticated(authState === 'signedIn')
  }, [authState])


  const navigationContext = useMemo(() => ({
    isSearchOpen,
    setSearchOpen,
    isMobileCategoryBrowserOpen,
    setMobileCategoryBrowserOpen,
  }), [isSearchOpen, isMobileCategoryBrowserOpen])

  // Compile the categories heirarchy
  const categories = useMemo(() => (
    <MegaMenuContextProvider>
      <NavDropdownMegaMenu wrapper>
        {header && header?.menu?.items && header.menu?.items.map(({ name, children }, menuIdx) => {
          return (
            <StyledNavDropdownMegaMenuItem item
              key={menuIdx}
              idx={menuIdx}
              title={name as string}
              aria-haspopup={children && children.length > 1 ? 'true' : 'false'}
            >
            </StyledNavDropdownMegaMenuItem>
          )
        })}
      </NavDropdownMegaMenu>
      {header && header?.menu?.items && header.menu?.items.map(({ name, children, ...menuItem1 }, headerMenuIdx) => {
        const href = getUrlFromMenuItem(menuItem1)

        return (
          <StyledNavDropdownMegaMenuTabs tabs
            key={headerMenuIdx}
            idx={headerMenuIdx}
          >
            <Grid container direction="row">
              <StyledMenuGrid item xs={3}>
                <Grid container direction="column">
                  {href && href !== '#' && (
                    <Grid item>
                      <MegaMenuMuiLink href={href}>All {name}</MegaMenuMuiLink>
                    </Grid>
                  )}
                  {children &&
                    // eslint-disable-next-line @typescript-eslint/no-shadow
                    children.map(({ name }, idx2) => (
                      <Grid item key={idx2}>
                        <NavDropdownMegaMenu tab idx={idx2}>
                          {name}
                        </NavDropdownMegaMenu>
                      </Grid>
                    ))}
                </Grid>
              </StyledMenuGrid>
              <Grid item xs={9}>
                {children &&
                  // eslint-disable-next-line @typescript-eslint/no-shadow
                  children.map(({ name, children, ...menuItem }, idx3) => {
                    const menuItemHref = getUrlFromMenuItem(menuItem)
                    // eslint-disable-next-line @typescript-eslint/no-shadow
                    const subMenu = children?.map(({ name, children, ...menuItem }) => {
                      const subMenuHref = getUrlFromMenuItem(menuItem)
                      // eslint-disable-next-line @typescript-eslint/no-shadow
                      const subMenu2 = children?.map(({ name, ...menuItem }) => ({
                        name,
                        href: getUrlFromMenuItem(menuItem),
                      })) || []

                      return {
                        name,
                        href: subMenuHref,
                        subMenu: subMenu2,
                      }
                    }) || []

                    return children && (
                      <NavDropdownMegaMenu tabpanel idx={idx3} key={idx3} >
                        <CategoryTabPanel subMenu={subMenu} name={name} href={menuItemHref} />
                      </NavDropdownMegaMenu>
                    )
                  },
                  )}
              </Grid>
            </Grid>
          </StyledNavDropdownMegaMenuTabs>
        )
      })}
    </MegaMenuContextProvider>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [header?.menu?.items])

  return (
    <PrimaryNavigationContext.Provider value={navigationContext}>
      <MobileSearchBarModal id="search" />
      <PrimaryNavigation
        onAuthModalOpen={() => onOpenAuthDialog()}
        authenticated={authenticated}
        categoryChildren={categories}
        {...props}
      />
      {isMobileCategoryBrowserOpen && <StyledModalBackground onClick={onCloseMobileCategoryBrowser} $isGlobalOpen={isMobileCategoryBrowserOpen} />}
    </PrimaryNavigationContext.Provider>
  )
}

const StyledMenuGrid = styled(Grid)`
  border-right: 1px solid ${(props) => props.theme.palette.divider};
`

const StyledNavDropdownMegaMenuItem = styled(NavDropdownMegaMenu)`
  height: ${pxToRem(70)};
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up('md')} {
    height: ${pxToRem(80)};
  }
`

const StyledNavDropdownMegaMenuTabs = styled(NavDropdownMegaMenu)`
  top: ${pxToRem(69)};

  ${({ theme }) => theme.breakpoints.up('md')} {
    top: ${pxToRem(79)};
  }
`